import React from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import DraggableWorkflowItem, { WorkflowItem } from "./DraggableWorkflowItem";
import { cn } from "@/utils/utils";
import { useReorderQuestionnairesMutation } from "@/pages/deals/hooks";

interface Props {
  items: WorkflowItem[];
  onItemsChange?: (items: WorkflowItem[]) => void;
  className?: string;
  isDragDisabled?: boolean;
  stageId?: string;
  onReorder?: (orderedIds: string[]) => void | Promise<void>;
}

const DraggableWorkflowListContainer: React.FC<Props> = ({
  items,
  onItemsChange,
  className = "",
  isDragDisabled = false,
  stageId,
  onReorder
}) => {
  const reorderMutation = useReorderQuestionnairesMutation();

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    const newItems = [...items];
    const [movedItem] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, movedItem);

    if (onItemsChange) {
      onItemsChange(newItems);
    }

    const orderedIds = newItems.map((item) => item.id.toString());

    if (onReorder) {
      Promise.resolve(onReorder(orderedIds)).catch((error) => {
        console.error("Failed to reorder workflows:", error);
      });
      return;
    }

    if (stageId) {
      reorderMutation.mutate(
        {
          pathParams: { stageId },
          data: orderedIds
        },
        {
          onError: (error) => {
            console.error("Failed to reorder questionnaires:", error);
          }
        }
      );
    }
  };

  const transformedItems: WorkflowItem[] = items.map((questionnaire) => ({
    ...questionnaire,
    id: questionnaire.id.toString()
  }));

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        droppableId="workflows-list"
        isDropDisabled={isDragDisabled || items.length <= 1}
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "rounded-2xl border border-border-border1 bg-surface-widget-card overflow-hidden",
              className
            )}
          >
            {transformedItems.map((item, index) => (
              <DraggableWorkflowItem
                key={item.id}
                item={item}
                index={index}
                isDragDisabled={isDragDisabled || items.length <= 1}
                isLast={index === items.length - 1}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableWorkflowListContainer;
